import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
    mdiFolderOutline,
    mdiFolderOpenOutline,
    mdiFolderQuestionOutline,
    mdiShieldAccount,
    mdiChevronRight,
    mdiChevronDown,
} from '@mdi/js'
import { AppIcon } from '../ui/icon'
import type { AssetDocumentFolder } from '../../hooks/useQueries'
import { getLocalizedName } from '../../lib/localization'
import { groupDocumentsByFolder, type GroupableDocument } from '../../lib/document-folders'

/**
 * Read-only rendering of the document folder hierarchy resolved for an asset
 * (spec 2026-07-27-06). Folders are never creatable/editable from here — the
 * layout admin page (`/$org/folder-layouts`) remains the only place that edits
 * structure.
 *
 * The component owns the ordering / nesting / unclassified-bucket traversal and
 * delegates the actual markup of each document to `renderDocument`, so both the
 * asset documents tab (list of divs) and the global documents table (`<tr>`
 * rows) share one implementation. `renderFolder` overrides the default folder
 * header the same way; when it is provided the component emits nothing of its
 * own, which is what makes the `<tbody>` usage legal.
 */

export interface DocumentFolderHeaderInfo {
    folder: AssetDocumentFolder
    depth: number
    /**
     * Either `documents.length` (this exact folder only) or the folder's
     * rolled-up count (this folder plus every descendant), per the tree's
     * `countMode` prop — default `'own'`.
     *
     * Deliberately counted client-side from `documents`/`rolledUpCount` rather
     * than taken from the endpoint's `folder.document_count`: the tree also
     * renders *filtered* slices (the global documents list applies type/flag
     * filters and its own page size), where the server-side count of the
     * asset's documents would contradict the rows actually shown. Callers
     * that want the full picture must pass the full document set — see
     * AssetDocuments, which asks for the API's maximum page size for exactly
     * that reason.
     *
     * `'own'` is the default because the global documents list
     * (`DocumentsPage.tsx`) renders filtered/paginated slices, where a
     * rolled-up count could show more than the rows actually on screen — an
     * internal contradiction (spec 2026-08-01-20, decision 2). `AssetDocuments`
     * has no such filtering (always the full per-asset set) and opts into
     * `'rolledUp'` so a collapsed parent's number reflects everything inside
     * it, not just documents typed to that exact folder.
     */
    count: number
    label: string
    /**
     * Whether this folder's documents (and its child folders) are currently
     * shown. Folders start **collapsed** — see `DocumentFolderTree`.
     *
     * Part of the header info rather than of the default markup on purpose:
     * `DocumentsPage` replaces the header with its own `<tr>` and must be able
     * to render the same toggle, so the state has to live in the tree.
     */
    expanded: boolean
    /** Flip this folder between expanded and collapsed. */
    onToggle: () => void
    /**
     * Outline number of the folder in the layout (`1`, `1.1`, `2` …), derived
     * by `groupDocumentsByFolder` from the same helper the folder-layouts
     * editor uses, so both screens name a folder identically.
     *
     * `undefined` for the synthetic unclassified bucket, which is not part of
     * any layout and therefore consumes no number — custom renderers must
     * handle its absence.
     */
    number?: string
}

interface DocumentFolderTreeProps<D extends GroupableDocument> {
    folders: AssetDocumentFolder[]
    documents: D[]
    renderDocument: (doc: D, depth: number) => ReactNode
    renderFolder?: (info: DocumentFolderHeaderInfo) => ReactNode
    /** Hide folders that ended up with no document (default: false). */
    hideEmptyFolders?: boolean
    /**
     * Which number a folder header's `count` shows: `'own'` (documents typed
     * to this exact folder, excluding descendants) or `'rolledUp'` (this
     * folder plus every descendant). Default `'own'` — see the rationale on
     * `DocumentFolderHeaderInfo.count`.
     */
    countMode?: 'own' | 'rolledUp'
}

const INDENT_PX = 16

/** Synthetic uid of the trailing "documents matching no folder" bucket. */
export const UNCLASSIFIED_FOLDER_UID = 'unclassified'

export function DocumentFolderTree<D extends GroupableDocument>({
    folders,
    documents,
    renderDocument,
    renderFolder,
    hideEmptyFolders = false,
    countMode = 'own',
}: DocumentFolderTreeProps<D>) {
    const { t, i18n } = useTranslation('folders')
    const { rows, unclassified } = groupDocumentsByFolder(folders, documents)

    /**
     * Expanded folder uids — everything starts collapsed (spec 2026-08-01-20),
     * including the unclassified bucket, so a layout with many folders is a
     * short outline instead of a wall of "No document" placeholders. The set
     * holds the *expanded* folders rather than the collapsed ones so a layout
     * that grows new folders never silently expands them.
     */
    const [expandedUids, setExpandedUids] = useState<Set<string>>(() => new Set())

    const toggleFolder = useCallback((uid: string) => {
        setExpandedUids(prev => {
            const next = new Set(prev)
            if (!next.delete(uid)) next.add(uid)
            return next
        })
    }, [])

    // Collapsing a parent hides its whole subtree, so a row is visible only
    // when every ancestor is expanded.
    const parentByUid = useMemo(() => {
        const map = new Map<string, string | null>()
        for (const folder of folders) map.set(folder.uid, folder.parent_folder_uid ?? null)
        return map
    }, [folders])

    const isVisible = useCallback(
        (uid: string) => {
            let parent = parentByUid.get(uid) ?? null
            const guard = new Set<string>([uid])
            while (parent) {
                if (guard.has(parent)) return true // cycle guard, mirrors groupDocumentsByFolder
                guard.add(parent)
                if (!expandedUids.has(parent)) return false
                parent = parentByUid.get(parent) ?? null
            }
            return true
        },
        [parentByUid, expandedUids]
    )

    const defaultFolderHeader = ({ folder, depth, count, label, number, expanded, onToggle }: DocumentFolderHeaderInfo) => (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="flex w-full items-center gap-2 py-2 text-left text-sm font-medium text-foreground hover:bg-muted/50 rounded"
            style={{ paddingLeft: depth * INDENT_PX }}
            data-testid={`document-folder-${folder.uid}`}
            data-folder-code={folder.code}
        >
            <AppIcon tone="muted" path={expanded ? mdiChevronDown : mdiChevronRight} size="sm" className="shrink-0" />
            {number && (
                <span
                    className="text-xs text-muted-foreground font-mono shrink-0 tabular-nums"
                    data-testid={`document-folder-number-${folder.uid}`}
                >
                    {number}
                </span>
            )}
            <AppIcon tone="muted" path={expanded ? mdiFolderOpenOutline : mdiFolderOutline} size="md" className="shrink-0" />
            <span>{label}</span>
            {/* PII marker (spec 2026-08-13-02 §8): the folder's document-type
                subtree contains at least one `pii`-flagged type, i.e. this is
                where personal data lands. Computed server-side, never stored. */}
            {folder.pii && (
                <AppIcon
                    path={mdiShieldAccount}
                    size="sm"
                    className="text-warning shrink-0"
                    title={t('pii.folder_marker', { ns: 'common' })}
                    data-testid={`document-folder-pii-${folder.uid}`}
                />
            )}
            <span className="text-xs text-muted-foreground" data-testid={`document-folder-count-${folder.uid}`}>
                {count}
            </span>
        </button>
    )

    const folderHeader = renderFolder ?? defaultFolderHeader

    const defaultUnclassifiedHeader = ({ count, label, expanded, onToggle }: DocumentFolderHeaderInfo) => (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="flex w-full items-center gap-2 py-2 text-left text-sm font-medium text-muted-foreground hover:bg-muted/50 rounded"
            data-testid="document-folder-unclassified"
        >
            <AppIcon
                path={expanded ? mdiChevronDown : mdiChevronRight}
                size="sm"
                className="shrink-0"
            />
            <AppIcon path={mdiFolderQuestionOutline} size="md" className="shrink-0" />
            <span>{label}</span>
            <span className="text-xs" data-testid="document-folder-count-unclassified">{count}</span>
        </button>
    )

    // The unclassified bucket is modelled as a synthetic folder so custom
    // renderers get exactly the same shape for it as for a real folder.
    const unclassifiedFolder: AssetDocumentFolder = {
        uid: UNCLASSIFIED_FOLDER_UID,
        code: UNCLASSIFIED_FOLDER_UID,
        name: {},
        ordering: Number.MAX_SAFE_INTEGER,
        document_type_uids: [],
        document_count: unclassified.length,
        // The bucket claims no document type, so it can never be PII-relevant.
        pii: false,
    }

    return (
        <>
            {rows.map(({ folder, depth, number, documents: folderDocs, rolledUpCount }) => {
                if (hideEmptyFolders && folderDocs.length === 0) return null
                if (!isVisible(folder.uid)) return null
                const expanded = expandedUids.has(folder.uid)
                const info: DocumentFolderHeaderInfo = {
                    folder,
                    depth,
                    number,
                    count: countMode === 'rolledUp' ? rolledUpCount : folderDocs.length,
                    label: getLocalizedName(folder.name as Record<string, string>, i18n.language) || folder.code,
                    expanded,
                    onToggle: () => toggleFolder(folder.uid),
                }
                return (
                    <FolderSection key={folder.uid}>
                        {folderHeader(info)}
                        {!expanded ? null : folderDocs.length === 0 ? (
                            renderFolder ? null : (
                                <div
                                    className="text-xs text-muted-foreground italic py-1"
                                    style={{ paddingLeft: (depth + 1) * INDENT_PX + 8 }}
                                    data-testid={`document-folder-empty-${folder.uid}`}
                                >
                                    {t('asset_view.folder_empty')}
                                </div>
                            )
                        ) : (
                            folderDocs.map(doc => renderDocument(doc, depth + 1))
                        )}
                    </FolderSection>
                )
            })}

            {unclassified.length > 0 && (
                <FolderSection>
                    {(renderFolder ?? defaultUnclassifiedHeader)({
                        folder: unclassifiedFolder,
                        depth: 0,
                        // No `number`: the bucket is not in the layout, so it
                        // takes no place in the outline numbering.
                        count: unclassified.length,
                        label: t('asset_view.unclassified'),
                        expanded: expandedUids.has(UNCLASSIFIED_FOLDER_UID),
                        onToggle: () => toggleFolder(UNCLASSIFIED_FOLDER_UID),
                    })}
                    {expandedUids.has(UNCLASSIFIED_FOLDER_UID) &&
                        unclassified.map(doc => renderDocument(doc, 1))}
                </FolderSection>
            )}
        </>
    )
}

/**
 * Fragment wrapper: keeps the tree agnostic of its container so it can emit
 * `<tr>` rows inside a `<tbody>` just as well as divs inside a list.
 */
function FolderSection({ children }: { children: ReactNode }) {
    return <>{children}</>
}
